import { useEffect, useCallback } from 'react'
import { Flex, MuiDatePicker, Text, Box } from '@pinchenterprisesnpm/friday-ui'
import { FormikProps } from 'formik'
import { endOfDay, isBefore } from 'date-fns'
import { INewDuplicateModalFormValues } from '@src/features/shift-post-v2/modals/new-duplicate-modal/new-duplicate-modal/new-duplicate-modal.form'
import { INewEditModalFormValues } from '@src/features/shift-post-v2/modals/new-edit-modal/new-edit-modal/new-edit-modal.form'
import { IScheduleSectionFormValues } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/schedule-section.types'
import { SHIFT_PROGRESS, SHIFT_TYPE } from '@src/models/Shift'
import { FEATURE_TOGGLE_MULTIDAY_REVAMP } from '@src/config/featureToggles'
import { ShiftPostPage } from '@src/features/shift-post-v2/pages'
import { isBeforeToday } from 'hooks/useDatePickerRange'
import { CustomValidationError } from 'features/shift-post-v2/style'
import { WeekdaySelector } from './components/weekday-selector'
import { generateRecurringDays } from './recurring-day-picker.utils'

const { DatePickerSingle } = MuiDatePicker

export interface IRecurringDayPickerProps {
  formik: FormikProps<
    | INewDuplicateModalFormValues
    | INewEditModalFormValues
    | IScheduleSectionFormValues
  >
  isDisabled?: boolean
  sourcePage: ShiftPostPage
}

export const RecurringDayPicker = ({
  formik,
  isDisabled,
  sourcePage,
}: IRecurringDayPickerProps) => {
  const { values, setFieldValue, errors } = formik

  const isEditInProgressMultidayShift =
    Number(values?.type) === SHIFT_TYPE.MULTIDAY &&
    Number(values?.progress) === SHIFT_PROGRESS.ACTIVE &&
    FEATURE_TOGGLE_MULTIDAY_REVAMP &&
    sourcePage === ShiftPostPage.EDIT_SHIFT

  const handleStartDateChange = useCallback(
    (selectedDay: Date) => {
      if (isBeforeToday(selectedDay)) return

      setFieldValue('multiday_day_start', selectedDay)
    },
    [setFieldValue],
  )

  const handleEndDateChange = useCallback(
    (selectedDay: Date) => {
      if (isBeforeToday(selectedDay)) return

      setFieldValue('multiday_day_end', selectedDay)
    },
    [setFieldValue],
  )

  useEffect(() => {
    /**
     * We always refresh the days when the start or end date changes
     * or when the weekdays change
     */
    if (values.multiday_day_start && values.multiday_day_end) {
      const days = generateRecurringDays(
        values.multiday_day_start,
        values.multiday_day_end,
        values.selected_weekdays,
        values,
      )

      setFieldValue('days', days)
    }
    // Only re-render when the start, end date or weekdays change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.multiday_day_start,
    values.multiday_day_end,
    values.selected_weekdays,
  ])

  const handleStartDateReset = useCallback(() => {
    setFieldValue('multiday_day_start', undefined)
    setFieldValue('days', [])
  }, [setFieldValue])

  const handleEndDateReset = useCallback(() => {
    setFieldValue('multiday_day_end', undefined)
    setFieldValue('days', [])
  }, [setFieldValue])

  const handleWeekdayToggle = useCallback(
    (day: string) => {
      const newSelectedWeekdays = values.selected_weekdays.includes(day)
        ? values.selected_weekdays.filter((d: string) => d !== day)
        : [...values.selected_weekdays, day]

      setFieldValue('selected_weekdays', newSelectedWeekdays)
      // Regenerate days if we have a date range selected
      if (values.multiday_day_start && values.multiday_day_end) {
        const days = generateRecurringDays(
          values.multiday_day_start,
          values.multiday_day_end,
          newSelectedWeekdays,
          values,
        )

        setFieldValue('days', days)
      }
    },
    [setFieldValue, values],
  )

  return (
    <Flex flexDirection="column">
      <Flex gap="md">
        <Box width="50%">
          <Text mb="xs" mt={0}>
            Start Date
          </Text>
          <DatePickerSingle
            selectedDay={values.multiday_day_start}
            onDayClick={handleStartDateChange}
            onReset={handleStartDateReset}
            disabledDays={(date: Date) => {
              return isBefore(endOfDay(date), endOfDay(new Date()))
            }}
            isDisabled={isDisabled}
          />
          {errors.multiday_day_start &&
          typeof errors.multiday_day_start === 'string' ? (
            <CustomValidationError mt="xs" mb={0}>
              {errors.multiday_day_start}
            </CustomValidationError>
          ) : null}
        </Box>

        <Box width="50%">
          <Text mb="xs" mt={0}>
            End Date
          </Text>
          <DatePickerSingle
            selectedDay={values.multiday_day_end}
            onDayClick={handleEndDateChange}
            onReset={handleEndDateReset}
            disabledDays={(date: Date) => {
              return (
                isBefore(endOfDay(date), endOfDay(new Date())) ||
                (values.multiday_day_start
                  ? date < values.multiday_day_start
                  : false)
              )
            }}
            isDisabled={isDisabled}
          />
          {errors.multiday_day_end &&
          typeof errors.multiday_day_end === 'string' ? (
            <CustomValidationError mt="xs" mb={0}>
              {errors.multiday_day_end}
            </CustomValidationError>
          ) : null}
        </Box>
      </Flex>

      <Box mt="md">
        <Text mb="xs">Select Weekdays</Text>
        <WeekdaySelector
          selectedDays={values.selected_weekdays}
          onDayToggle={handleWeekdayToggle}
          error={errors.selected_weekdays}
          isDisabled={isEditInProgressMultidayShift}
        />
        {errors.days && typeof errors.days === 'string' && (
          <CustomValidationError mt="xs" mb={0}>
            {errors.days}
          </CustomValidationError>
        )}
      </Box>
    </Flex>
  )
}
